import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Copy, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Pill } from "@/components/common";
import { SettingsTabs } from "@/components/settings/SettingsTabs";
import { Button } from "@/components/ui/button";

const TplRow = ({ t, onChanged }) => {
  const call = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const duplicate = () => call(() => api.post("/checklist-templates", { name: `${t.name} (copy)`, fields: t.fields, sections: t.sections }), "List duplicated");
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0" data-testid={`checklist-tpl-row-${t.id}`}>
      <span className="w-9 h-9 rounded-xl bg-[#EAF2F2] text-[#24474A] grid place-items-center shrink-0"><ListChecks className="w-4 h-4" /></span>
      <div className="min-w-0 flex-1">
        <Link to={`/settings/checklists/${t.id}`} className="font-bold text-slate-800 hover:text-[#24474A]" data-testid={`checklist-tpl-name-${t.id}`}>{t.name}</Link>
        <div className="text-xs text-slate-500">{t.sections.length} section{t.sections.length === 1 ? "" : "s"} · {t.count} item{t.count === 1 ? "" : "s"}</div>
      </div>
      <Pill cls="bg-slate-50 text-slate-600 border-slate-200">{t.project_count} project(s)</Pill>
      <div className="flex gap-1">
        <Button size="icon" variant="ghost" asChild data-testid={`checklist-tpl-edit-${t.id}`}><Link to={`/settings/checklists/${t.id}`}><Pencil className="w-4 h-4" /></Link></Button>
        <Button size="icon" variant="ghost" onClick={duplicate} data-testid={`checklist-tpl-duplicate-${t.id}`}><Copy className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => window.confirm(`Delete ${t.name}?`) && call(() => api.delete(`/checklist-templates/${t.id}`), "List deleted")} data-testid={`checklist-tpl-delete-${t.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
      </div>
    </div>
  );
};

export default function ChecklistsSettings() {
  const [list, setList] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(() => {
    api.get("/checklist-templates").then((r) => setList(r.data));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <PageHeader title="Settings" subtitle="Pre-defined task lists, like the Venue opening tracker. Pick one when creating a project.">
        <Button className="rounded-full" onClick={() => navigate("/settings/checklists/new")} data-testid="new-checklist-tpl-btn"><Plus className="w-4 h-4 mr-1.5" /> New list</Button>
      </PageHeader>
      <SettingsTabs />
      <div className="rounded-2xl border border-slate-200 bg-white" data-testid="checklist-tpl-list">
        {list?.map((t) => <TplRow key={t.id} t={t} onChanged={load} />)}
        {list === null && <div className="px-4 py-8 text-center text-sm text-slate-400">Loading…</div>}
        {list?.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-500">No lists yet.</div>}
      </div>
    </div>
  );
}
